import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      fetchCurrentUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      setOrganization(response.data.organization);
      setError(null);
    } catch (err) {
      console.error("Auth check failed:", err);
      localStorage.removeItem("auth_token");
      setUser(null);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token, user: userData, organization: orgData } = response.data;
      
      localStorage.setItem("auth_token", token);
      setUser(userData);
      setOrganization(orgData);
      
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.detail || "Login failed. Please try again.";
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/auth/register`, data);
      const { token, user: userData, organization: orgData } = response.data;
      
      localStorage.setItem("auth_token", token);
      setUser(userData);
      setOrganization(orgData);
      
      return { success: true, user: userData };
    } catch (err) {
      const message = err.response?.data?.detail || "Registration failed. Please try again.";
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_email");
      setUser(null);
      setOrganization(null);
    }
  }, []);

  const forgotPassword = async (email) => {
    try {
      setError(null);
      await axios.post(`${API}/auth/forgot-password`, { email });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to send reset email.";
      setError(message);
      return { success: false, error: message };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setError(null);
      await axios.post(`${API}/auth/reset-password`, { token, password });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to reset password.";
      setError(message);
      return { success: false, error: message };
    }
  };

  const updateProfile = async (data) => {
    try {
      setError(null);
      const token = localStorage.getItem("auth_token");
      const response = await axios.patch(`${API}/auth/me`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to update profile.";
      setError(message);
      return { success: false, error: message };
    }
  };

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const isStaff = user?.role === "staff";
  const isElevated = isAdmin || isStaff;
  const isMember = user?.role === "member" || user?.role === "team_user";

  // Impersonation: track whether we're currently logged in as another user.
  // When impersonation starts we stash the admin/staff token under `admin_token`
  // and swap `auth_token` to the new one. Stop just swaps back — instant.
  const isImpersonating = !!localStorage.getItem("admin_token");

  const startImpersonation = async (userId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(
        `${API}/auth/admin/impersonate/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Stash original elevated token, swap in the impersonation token.
      localStorage.setItem("admin_token", token);
      localStorage.setItem("admin_email", user?.email || "");
      localStorage.setItem("auth_token", res.data.token);
      // Pull fresh user/org context for the impersonated account.
      await fetchCurrentUser(res.data.token);
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.response?.data?.detail || "Impersonation failed" };
    }
  };

  const stopImpersonation = async () => {
    const adminToken = localStorage.getItem("admin_token");
    if (!adminToken) return { success: false };
    localStorage.setItem("auth_token", adminToken);
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    await fetchCurrentUser(adminToken);
    return { success: true };
  };

  const value = {
    user,
    organization,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin,
    isSuperAdmin,
    isStaff,
    isElevated,
    isImpersonating,
    isMember,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    getAuthHeader,
    startImpersonation,
    stopImpersonation,
    refreshUser: () => fetchCurrentUser(localStorage.getItem("auth_token")),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
